// src/components/create-project/ProjectForm/index.tsx
import { FC } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, Loader2, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProjectForm } from '../hooks/useProjectForm';
import { Step1ProjectInfo } from './steps/Step1ProjectInfo';
import { Step2UseCases } from './steps/Step2UseCases';
import { Step3ContentTypes } from './steps/Step3ContentTypes';
import { Step4ContentStructure } from './steps/Step4ContentStructure';
import { Step5Review } from './steps/Step5Review';
import { Project } from '../types/project';
import { STEPS } from '../utils/constants';


interface ProjectFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (project: Project) => void;
}

export const ProjectForm: FC<ProjectFormProps> = ({ open, onClose, onSuccess }) => {
  const {
    step,
    formData,
    errors,
    setStep,
    updateField,
    validateStep,
    handleSubmit,
    isLoading
  } = useProjectForm(onClose, onSuccess);

  const renderStep = () => {
    const props = { formData, updateField, errors };
    
    switch (step) {
      case 0: return <Step1ProjectInfo {...props} />;
      case 1: return <Step2UseCases {...props} />;
      case 2: return <Step3ContentTypes {...props} />;
      case 3: return <Step4ContentStructure {...props} />;
      case 4: return <Step5Review formData={formData} />;
      default: return null;
    }
  };

  const goBackInSteps = () => {
    if(step > 0){
      setStep(prev => prev - 1);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden bg-neutral-50 dark:bg-neutral-900">
        <div className="flex flex-col sm:flex-row max-h-[800px] overflow-auto">
          {/* Left Panel */}
          <div className="w-full sm:w-80 bg-neutral-100/50 dark:bg-neutral-800/50 p-4 sm:p-8 border-r border-neutral-200 dark:border-neutral-700 flex flex-col">
            {step > 0 && <Button variant="ghost" onClick={goBackInSteps} className="top-4 left-4 bg-white dark:bg-neutral-900 mb-5 w-10 h-10">
              <ChevronLeft className="h-4 w-4" />
            </Button>}
            <div className="space-y-6 flex-1">
              {STEPS.map((stepItem, index) => (
                <div
                  key={stepItem.title}
                  className={cn(
                    'relative flex items-start space-x-4',
                    index !== STEPS.length - 1 &&
                      'after:absolute after:left-[15px] after:top-[40px] after:h-[calc(100%+8px)] after:w-[2px] after:bg-neutral-200 dark:after:bg-neutral-700',
                    index < step && 'after:bg-primary-300 dark:after:bg-primary-600'
                  )}
                >
                  <div
                    className={cn(
                      'relative z-20 flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white dark:bg-neutral-900',
                      index < step
                        ? 'border-primary-400 bg-primary-400 text-white dark:border-primary-500 dark:bg-primary-500'
                        : index === step
                        ? 'border-primary-400 text-primary-400 dark:border-primary-500 dark:text-primary-500'
                        : 'border-neutral-300 text-neutral-300 dark:border-neutral-600 dark:text-neutral-600'
                    )}
                  >
                    {index < step ? <Check className="h-4 w-4" /> : <span className="text-sm font-medium">{index + 1}</span>}
                  </div>
                  <div className="space-y-1">
                    <p className={cn(
                      'text-base font-medium leading-none',
                      index <= step
                        ? 'text-neutral-900 dark:text-neutral-100'
                        : 'text-neutral-400 dark:text-neutral-500'
                    )}>
                      {stepItem.title}
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{stepItem.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-700 mt-4">
              <Button
                variant="outline"
                className="w-full justify-between hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={onClose}
              >
                Cancel
                <Zap className="h-4 w-4" />
              </Button>
              <Button
                className="w-full justify-between bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                onClick={step < STEPS.length - 1 ? () => {
                  if (validateStep()) setStep(prev => prev + 1);
                } : handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : step < STEPS.length - 1 ? (
                  <>
                    Continue
                    <ChevronRight className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Create Project
                    <Check className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Right Panel */}
          <div className="flex-1 flex flex-col h-full bg-white dark:bg-neutral-900">
            <DialogHeader className="p-4 sm:p-8 pb-0">
              <DialogTitle className="text-lg sm:text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                <DialogDescription>
                  {STEPS[step].title}
                </DialogDescription>
              </DialogTitle>
              <p className="text-base text-neutral-500 dark:text-neutral-400">{STEPS[step].description}</p>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto p-4 sm:p-8 sm:pt-0">
              <form onSubmit={(e) => e.preventDefault()} className="space-y-4 sm:space-y-8">
                <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
              </form>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};